import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { useSearchHistoryStore, useUIStore } from '~/store';
import { NestedSearch, TODO, SearchTab } from '@habity/types';
import { omitNullKeys } from '~/lib/utils';
import { shallow } from 'zustand/shallow';
import isEqual from 'lodash/isEqual';
import { useHomesStore } from '~/store/useHomesStore';
import { useLocation } from './useLocation';
import {
  getFiltersFromServerSearchParams,
  getSearchPath,
  getTabNameFromUrlPath,
} from '@habity/services';
import { isSearchRoute } from '@habity/utils';

export const useFilters = () => {
  const { push, replace } = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const { fetchLocations } = useLocation();

  const storedFilters = useHomesStore((s) => s.filters);
  const locations = useHomesStore((s) => s.locations);
  const mapBounds = useHomesStore((s) => s.mapBounds);
  const setMapBounds = useHomesStore((s) => s.setMapBounds);
  const setFilters = useHomesStore((s) => s.setFilters);
  const setLocations = useHomesStore((s) => s.setLocations);
  const addToSearchHistory = useSearchHistoryStore(
    (state) => state.addToSearchHistory
  );

  const { setRangePickerValues } = useUIStore(
    (state) => ({
      loading: state.loading,
      setLoading: state.setLoading,
      setRangePickerValues: state.setRangePickerValues,
    }),
    shallow
  );
  const filtersEmpty = () => {
    const cleanedFilters = omitNullKeys(storedFilters);
    if (!cleanedFilters) return true;

    const locationFields = [
      'label',
      'street',
      'zip',
      'areaIds',
      'municipalityIds',
      'brokerIds',
      'longitude',
      'latitude',
      'streetIds',
    ];
    const filterKeys = Object.keys(cleanedFilters);

    return (
      filterKeys.length === 0 ||
      filterKeys.every((key) => locationFields.includes(key))
    );
  };

  const locationsEmpty =
    locations === null || (locations && locations.length === 0);
  const map = searchParams?.get('map') === 'true';

  const searchMapBounds = () => {
    const bounds = mapBounds as TODO; // TODO: Fix this type error
    if (!bounds || !bounds._sw || !bounds._ne) return;
    // addFilters({
    //   p1: [bounds._sw.lat, bounds._ne.lat, bounds._sw.lng, bounds._ne.lng],
    // });
    setMapBounds(null);
    setLocations([]);
  };

  const performSearch = ({
    filters = null,
    includeExistingFilters = true,
    ignoreSearch = false,
    tab,
    scrollToId,
  }: {
    filters?: Partial<NestedSearch> | null;
    includeExistingFilters?: boolean;
    ignoreSearch?: boolean;
    tab?: SearchTab;
    scrollToId?: string;
  }) => {
    const allFilters = includeExistingFilters
      ? { ...storedFilters, ...filters }
      : filters;
    const newFilters = omitNullKeys(allFilters) === null ? null : allFilters;

    // Before resetting filters, save them to history
    if (newFilters === null) addToSearchHistory(storedFilters);
    setFilters(newFilters);

    // If ignore search, stop here
    if (ignoreSearch) return;

    const searchPath = createSearchPathFromFilters({
      filters,
      includeExistingFilters,
      tab,
      scrollToId,
    });

    // Use replace on search pages to avoid page reload/flickering
    if (pathname && isSearchRoute(pathname)) {
      replace(searchPath);
    } else {
      push(searchPath);
    }
  };

  const createSearchPathFromFilters = ({
    filters = null,
    includeExistingFilters = true,
    tab,
    scrollToId,
  }: {
    filters?: Partial<NestedSearch> | null;
    includeExistingFilters?: boolean;
    tab?: SearchTab;
    scrollToId?: string;
  }) => {
    const allFilters = includeExistingFilters
      ? { ...storedFilters, ...filters }
      : filters;
    const newFilters = omitNullKeys(allFilters) === null ? null : allFilters;
    const currentTab = tab || getTabNameFromUrlPath(pathname);

    return getSearchPath({
      filters: newFilters,
      showMap: map, // Preserve map state regardless of tab
      tab: currentTab,
      scrollToId,
    });
  };

  const resetFilters = (ignoreSearch = false) => {
    // Reset range pickers and tabs
    setRangePickerValues(null);
    setLocations([]);
    // Reset filters to null (not undefined)
    setFilters(null);

    // If we should update the URL
    if (!ignoreSearch) {
      const searchPath = getSearchPath({
        filters: null,
        showMap: map,
        tab: getTabNameFromUrlPath(pathname),
      });

      // Always use replace to ensure consistent behavior
      if (pathname) {
        replace(searchPath);
      }
    }
  };

  const hasFilters = () =>
    storedFilters && Object.keys(storedFilters).length > 0;

  const applyFiltersFromQuery = async (
    // searchParams: ReadonlyURLSearchParams | null
    filters: Partial<NestedSearch>
  ) => {
    // TODO: Add filters from server search or useSearchParams ?
    const queryFilters = getFiltersFromServerSearchParams(filters);
    // Already applied filters or no query, do nothing
    if (
      omitNullKeys(queryFilters) === null ||
      isEqual(storedFilters, omitNullKeys(queryFilters))
    )
      return;
    // Set filters for visual feedback
    setFilters(queryFilters);
    // Fetch locations with new filters
    fetchLocations(queryFilters);
  };

  const getSearchRoute = (scrollToId?: string) =>
    getSearchPath({
      filters: storedFilters,
      tab: getTabNameFromUrlPath(window.location.pathname),
      scrollToId,
    });

  return {
    filters: storedFilters,
    hasFilters,
    resetFilters,
    locationsEmpty,
    filtersEmpty,
    performSearch,
    createSearchPathFromFilters,
    searchMapBounds,
    applyFiltersFromQuery,
    getSearchRoute,
  };
};
